// Firebase Realtime Database Service (tenant-scoped for SaaS)
import { 
  ref, 
  push, 
  set, 
  get, 
  update, 
  remove, 
  query, 
  orderByChild, 
  equalTo,
  onValue
} from 'firebase/database';
import { db } from './config';
import { auth } from './config';
import { sendPaymentReminderSMS } from './sms';
import { ACTIVITY_TYPES, diffMemberDetails, logMemberActivity } from './activityLog';
import {
  PAUSE_STATUS,
  addMonths,
  calculateMembershipFees,
  daysBetween,
  getActivePause,
  getDurationMonths,
  getEffectiveRegistrationDate,
  getOriginalRegistrationDate,
  getPauseHistory,
  isMembershipPaused,
  startOfDay,
  toDate,
  toIsoString
} from '../utils/membershipFees';

function tenantPath(tenantId, ...segments) {
  const path = ['tenants', tenantId, ...segments].filter(Boolean).join('/');
  return path;
}

// Add a new member
export const addMember = async (tenantId, memberData) => {
  try {
    const membersRef = ref(db, tenantPath(tenantId, 'members'));
    const newMemberRef = push(membersRef);
    
    // Calculate initial due date based on registration date and membership duration
    const registrationDate = memberData.registrationDate || new Date().toISOString();
    const membershipDuration = memberData.membershipDuration || 'monthly';
    const nextDueDate = calculateNextDueDate(registrationDate, membershipDuration);
    
    // Add the calculated due date to member data
    const memberDataWithDueDate = {
      ...memberData,
      registrationDate,
      nextDueDate,
      status: 'active'
    };
    
    await set(newMemberRef, memberDataWithDueDate);

    await logMemberActivity(tenantId, {
      memberId: newMemberRef.key,
      memberName: memberData.fullName,
      type: ACTIVITY_TYPES.MEMBER_CREATED,
      summary: 'Member registered'
    });

    return {
      success: true,
      memberId: newMemberRef.key,
      message: 'Member added successfully'
    };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

// Get all members
export const getAllMembers = async (tenantId) => {
  try {
    const membersRef = ref(db, tenantPath(tenantId, 'members'));
    const snapshot = await get(membersRef);
    
    if (snapshot.exists()) {
      const members = [];
      snapshot.forEach((childSnapshot) => {
        members.push({
          id: childSnapshot.key,
          ...childSnapshot.val()
        });
      });
      return {
        success: true,
        members: members
      };
    } else {
      return {
        success: true,
        members: []
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

// Get member by ID
export const getMemberById = async (tenantId, memberId) => {
  try {
    // Search by membershipId field instead of database key
    const membersRef = ref(db, tenantPath(tenantId, 'members'));
    const snapshot = await get(membersRef);
    
    if (snapshot.exists()) {
      let foundMember = null;
      let foundKey = null;
      
      snapshot.forEach((childSnapshot) => {
        const member = childSnapshot.val();
        if (member.membershipId === memberId) {
          foundMember = member;
          foundKey = childSnapshot.key;
        }
      });
      
      if (foundMember) {
        return {
          success: true,
          member: {
            id: foundKey,
            ...foundMember
          }
        };
      } else {
        return {
          success: false,
          message: 'Member not found'
        };
      }
    } else {
      return {
        success: false,
        message: 'Member not found'
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

// Update member
export const updateMember = async (tenantId, memberId, updates) => {
  try {
    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    await update(memberRef, updates);
    return {
      success: true,
      message: 'Member updated successfully'
    };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

/**
 * Permanently delete a member.
 *
 * Payment records are deliberately kept so historical revenue stays intact -
 * they are financial records, not member data. The deletion itself is written
 * to the activity log, which also survives.
 */
export const deleteMember = async (tenantId, memberId) => {
  try {
    if (!tenantId || !memberId) {
      return {
        success: false,
        error: 'invalid_request',
        message: 'Missing tenant or member reference'
      };
    }

    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const memberSnapshot = await get(memberRef);

    if (!memberSnapshot.exists()) {
      return {
        success: false,
        error: 'not_found',
        message: 'Member not found - it may already have been deleted.'
      };
    }

    const member = memberSnapshot.val();
    const billing = getMemberBillingSnapshot(member);

    await remove(memberRef);

    await logMemberActivity(tenantId, {
      memberId,
      memberName: member.fullName,
      type: ACTIVITY_TYPES.MEMBER_DELETED,
      summary:
        `Member deleted` +
        (billing.balanceDue > 0 ? ` with an outstanding balance of ₹${billing.balanceDue.toLocaleString()}` : ''),
      details: {
        membershipId: member.membershipId || null,
        balanceDueAtDeletion: billing.balanceDue,
        totalPaid: Number(member.totalPaid) || 0
      }
    });

    return {
      success: true,
      message: `${member.fullName || 'Member'} deleted successfully`,
      deletedMember: {
        fullName: member.fullName || '',
        membershipId: member.membershipId || null,
        balanceDue: billing.balanceDue
      }
    };
  } catch (error) {
    console.error('Error deleting member:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || getErrorMessage(error.code) || 'Failed to delete member'
    };
  }
};

// Count a member's payment records, so the delete confirmation can show what is retained
export const getMemberPaymentCount = async (tenantId, memberId) => {
  try {
    const result = await getMemberPayments(tenantId, memberId);
    if (!result.success) return { success: false, count: 0 };
    return { success: true, count: (result.payments || []).length };
  } catch (error) {
    return { success: false, count: 0 };
  }
};

// Search members by name
export const searchMembersByName = async (tenantId, searchTerm) => {
  try {
    const membersRef = ref(db, tenantPath(tenantId, 'members'));
    const searchQuery = query(membersRef, orderByChild('fullName'));
    const snapshot = await get(searchQuery);
    
    if (snapshot.exists()) {
      const members = [];
      snapshot.forEach((childSnapshot) => {
        const member = childSnapshot.val();
        if (member.fullName && member.fullName.toLowerCase().includes(searchTerm.toLowerCase())) {
          members.push({
            id: childSnapshot.key,
            ...member
          });
        }
      });
      return {
        success: true,
        members: members
      };
    } else {
      return {
        success: true,
        members: []
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

// Listen to members changes (real-time updates)
export const listenToMembers = (tenantId, callback) => {
  const membersRef = ref(db, tenantPath(tenantId, 'members'));
  
  const unsubscribe = onValue(membersRef, (snapshot) => {
    if (snapshot.exists()) {
      const members = [];
      snapshot.forEach((childSnapshot) => {
        members.push({
          id: childSnapshot.key,
          ...childSnapshot.val()
        });
      });
      callback({ success: true, members });
    } else {
      callback({ success: true, members: [] });
    }
  }, (error) => {
    callback({ 
      success: false, 
      error: error.code, 
      message: getErrorMessage(error.code) 
    });
  });

  return unsubscribe;
};

// Get member statistics
export const getMemberStats = async (tenantId) => {
  try {
    const membersRef = ref(db, tenantPath(tenantId, 'members'));
    const snapshot = await get(membersRef);
    
    if (snapshot.exists()) {
      const members = [];
      snapshot.forEach((childSnapshot) => {
        members.push(childSnapshot.val());
      });
      
      // Calculate total revenue from fees
      const totalRevenue = members.reduce((sum, member) => {
        const fees = member.fees;
        if (fees) {
          // Extract numeric value from fees string (e.g., "₹2000" -> 2000)
          const numericValue = parseFloat(fees.replace(/[^\d.]/g, ''));
          return sum + (isNaN(numericValue) ? 0 : numericValue);
        }
        return sum;
      }, 0);

      const stats = {
        totalMembers: members.length,
        activeMembers: members.filter(m => m.status === 'active').length,
        newMembersThisMonth: members.filter(m => {
          const regDate = new Date(m.registrationDate);
          const now = new Date();
          return regDate.getMonth() === now.getMonth() && 
                 regDate.getFullYear() === now.getFullYear();
        }).length,
        revenue: totalRevenue,
        membershipTypes: {
          student: members.filter(m => m.membershipLevel === 'student').length,
          adult: members.filter(m => m.membershipLevel === 'adult').length,
          individual: members.filter(m => m.membershipLevel === 'individual').length,
          old: members.filter(m => m.membershipLevel === 'old').length
        }
      };
      
      return {
        success: true,
        stats: stats
      };
    } else {
      return {
        success: true,
        stats: {
          totalMembers: 0,
          activeMembers: 0,
          newMembersThisMonth: 0,
          revenue: 0,
          membershipTypes: {
            student: 0,
            adult: 0,
            individual: 0,
            old: 0
          }
        }
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

// Helper function to get user-friendly error messages
const getErrorMessage = (errorCode) => {
  switch (errorCode) {
    case 'PERMISSION_DENIED':
      return 'Permission denied. Please check Firebase security rules.';
    case 'UNAVAILABLE':
      return 'Firebase service unavailable. Please check your internet connection.';
    case 'UNAUTHENTICATED':
      return 'Authentication required. Please log in again.';
    case 'DATA_STALE':
      return 'Data is stale. Please refresh and try again.';
    case 'MAX_RETRIES':
      return 'Maximum retries exceeded. Please try again later.';
    case 'OVERFLOWED_QUEUE':
      return 'Too many requests. Please try again later.';
    default:
      return 'An error occurred. Please try again.';
  }
};

// ==================== FEE MANAGEMENT FUNCTIONS ====================

// Record a fee payment
export const recordFeePayment = async (tenantId, memberId, paymentData) => {
  try {
    console.log('Starting payment recording for member:', memberId);
    
    // Check if user is authenticated
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }
    console.log('User authenticated:', auth.currentUser.email);
    
    const payment = {
      ...paymentData,
      paymentDate: new Date().toISOString(),
      timestamp: Date.now(),
      status: 'completed'
    };

    console.log('Payment object to save:', payment);

    // Add payment to payments collection
    const paymentsRef = ref(db, tenantPath(tenantId, 'payments'));
    const newPaymentRef = push(paymentsRef);
    console.log('Payment reference created:', newPaymentRef.key);
    
    await set(newPaymentRef, payment);
    console.log('Payment saved successfully');

    // Update member's last payment date and next due date
    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    console.log('Updating member:', memberId);
    
    const memberSnapshot = await get(memberRef);
    
    if (memberSnapshot.exists()) {
      const member = memberSnapshot.val();
      const membershipDuration = member.membershipDuration || 'monthly';
      
      // Calculate next due date based on current due date and membership duration
      const nextDueDate = calculateNextDueDateAfterPayment(member.nextDueDate || new Date().toISOString(), member.registrationDate || new Date().toISOString(), membershipDuration);
      
      // Calculate total paid and remaining balance
      const currentTotalPaid = member.totalPaid || 0;
      const newTotalPaid = currentTotalPaid + payment.amount;
      const monthlyFee = parseFloat(member.fees) || 0;
      const remainingBalance = Math.max(0, monthlyFee - newTotalPaid);
      const paymentStatus = remainingBalance === 0 ? 'fully_paid' : 'partial';
      
      const memberUpdates = {
        lastPaymentDate: payment.paymentDate,
        nextDueDate: nextDueDate,
        paymentStatus: paymentStatus,
        lastPaymentAmount: payment.amount,
        totalPaid: newTotalPaid,
        remainingBalance: remainingBalance
      };
      
      console.log('Updating member with:', memberUpdates);
      console.log('Payment calculation:', {
        currentTotalPaid,
        newPaymentAmount: payment.amount,
        newTotalPaid,
        monthlyFee,
        remainingBalance,
        paymentStatus
      });
      
      await update(memberRef, memberUpdates);
      console.log('Member updated successfully with balance information');
    } else {
      console.log('Member not found:', memberId);
    }

    return {
      success: true,
      paymentId: newPaymentRef.key,
      message: 'Payment recorded successfully'
    };
  } catch (error) {
    console.error('Error in recordFeePayment:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || getErrorMessage(error.code) || 'An error occurred while recording the payment'
    };
  }
};

// Get all payments for a member
export const getMemberPayments = async (tenantId, memberId) => {
  try {
    const paymentsRef = ref(db, tenantPath(tenantId, 'payments'));
    const memberPaymentsQuery = query(paymentsRef, orderByChild('memberId'), equalTo(memberId));
    const snapshot = await get(memberPaymentsQuery);
    
    if (snapshot.exists()) {
      const payments = [];
      snapshot.forEach((childSnapshot) => {
        payments.push({
          id: childSnapshot.key,
          ...childSnapshot.val()
        });
      });
      
      // Sort by payment date (newest first)
      payments.sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));
      
      return {
        success: true,
        payments: payments
      };
    } else {
      return {
        success: true,
        payments: []
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

// Get all payments (for admin dashboard)
export const getAllPayments = async (tenantId) => {
  try {
    const paymentsRef = ref(db, tenantPath(tenantId, 'payments'));
    const snapshot = await get(paymentsRef);
    
    if (snapshot.exists()) {
      const payments = [];
      snapshot.forEach((childSnapshot) => {
        payments.push({
          id: childSnapshot.key,
          ...childSnapshot.val()
        });
      });
      
      // Sort by payment date (newest first)
      payments.sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));
      
      return {
        success: true,
        payments: payments
      };
    } else {
      return {
        success: true,
        payments: []
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

// Get members with overdue payments
/**
 * Billing view of a member, derived from the same accrual engine the member
 * profile uses. Paused memberships accrue nothing, so they are never overdue
 * and never due for a reminder.
 */
export const getMemberBillingSnapshot = (member) => {
  const fees = calculateMembershipFees(member);
  const isPaused = isMembershipPaused(member);

  // Prefer the computed next charge date; fall back to the stored one
  const nextDueDate = isPaused ? null : (fees.nextChargeDate || member.nextDueDate || null);
  const dueDate = toDate(nextDueDate);

  return {
    fees,
    isPaused,
    nextDueDate,
    dueDate,
    balanceDue: fees.balanceDue,
    // Charges are levied at the start of each cycle, so anything unpaid is already due
    isOverdue: !isPaused && fees.balanceDue > 0
  };
};

const collectMembers = (snapshot) => {
  const members = [];
  snapshot.forEach((childSnapshot) => {
    members.push({ id: childSnapshot.key, ...childSnapshot.val() });
  });
  return members;
};

export const getOverdueMembers = async (tenantId) => {
  try {
    const membersRef = ref(db, tenantPath(tenantId, 'members'));
    const snapshot = await get(membersRef);

    if (snapshot.exists()) {
      const members = collectMembers(snapshot)
        .map((member) => ({ member, billing: getMemberBillingSnapshot(member) }))
        .filter(({ billing }) => billing.isOverdue)
        .map(({ member, billing }) => ({
          ...member,
          balanceDue: billing.balanceDue,
          totalCharged: billing.fees.totalCharged,
          nextDueDate: billing.nextDueDate
        }))
        // Largest outstanding balance first
        .sort((a, b) => b.balanceDue - a.balanceDue);

      return {
        success: true,
        members: members
      };
    } else {
      return {
        success: true,
        members: []
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

// Get members with upcoming due dates (within next 7 days)
export const getUpcomingDueMembers = async (tenantId) => {
  try {
    const membersRef = ref(db, tenantPath(tenantId, 'members'));
    const snapshot = await get(membersRef);
    
    if (snapshot.exists()) {
      const now = new Date();
      const sevenDaysFromNow = new Date(now.getTime() + (7 * 24 * 60 * 60 * 1000));

      // Paid-up members whose next cycle starts within a week (paused members excluded)
      const members = collectMembers(snapshot)
        .map((member) => ({ member, billing: getMemberBillingSnapshot(member) }))
        .filter(
          ({ billing }) =>
            !billing.isPaused &&
            !billing.isOverdue &&
            billing.dueDate &&
            billing.dueDate >= now &&
            billing.dueDate <= sevenDaysFromNow
        )
        .map(({ member, billing }) => ({
          ...member,
          nextDueDate: billing.nextDueDate,
          upcomingAmount: billing.fees.feeAmount
        }))
        .sort((a, b) => new Date(a.nextDueDate) - new Date(b.nextDueDate));

      return {
        success: true,
        members: members
      };
    } else {
      return {
        success: true,
        members: []
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

// Get members due for reminder (2 days before due date)
export const getMembersDueForReminder = async (tenantId) => {
  try {
    const membersRef = ref(db, tenantPath(tenantId, 'members'));
    const snapshot = await get(membersRef);
    
    if (snapshot.exists()) {
      const now = new Date();
      const twoDaysFromNow = new Date(now.getTime() + (2 * 24 * 60 * 60 * 1000));

      // Reminder candidates: due within 2 days and not paused
      const members = collectMembers(snapshot)
        .map((member) => ({ member, billing: getMemberBillingSnapshot(member) }))
        .filter(
          ({ billing }) =>
            !billing.isPaused &&
            billing.dueDate &&
            billing.dueDate >= now &&
            billing.dueDate <= twoDaysFromNow
        )
        .map(({ member, billing }) => ({ ...member, nextDueDate: billing.nextDueDate }))
        .sort((a, b) => new Date(a.nextDueDate) - new Date(b.nextDueDate));

      return {
        success: true,
        members: members
      };
    } else {
      return {
        success: true,
        members: []
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

// Send payment reminder with actual notification
export const sendPaymentReminder = async (tenantId, memberId, reminderType = 'due') => {
  try {
    // Get member details first
    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const memberSnapshot = await get(memberRef);
    
    if (!memberSnapshot.exists()) {
      return {
        success: false,
        message: 'Member not found'
      };
    }
    
    const member = memberSnapshot.val();

    // A paused membership is not accruing fees, so chasing payment would be wrong
    if (isMembershipPaused(member)) {
      return {
        success: false,
        error: 'membership_paused',
        message: `${member.fullName || 'This member'} has a paused membership - no payment reminder sent.`
      };
    }

    const billing = getMemberBillingSnapshot(member);
    const dueDate = toDate(billing.nextDueDate) || new Date(member.nextDueDate);
    const daysUntilDue = Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24));
    
    // Create reminder message based on type
    let reminderMessage = '';
    let subject = '';
    
    switch (reminderType) {
      case 'reminder':
        subject = 'Payment Reminder - Due in 2 days';
        reminderMessage = `Dear ${member.fullName},\n\nThis is a friendly reminder that your gym membership payment of ₹${member.fees} is due on ${dueDate.toLocaleDateString()} (in ${daysUntilDue} days).\n\nPlease ensure timely payment to avoid any interruption in your membership.\n\nBest regards,\nDE MUSCLE FACTORY Team`;
        break;
      case 'overdue':
        subject = 'Payment Overdue - Urgent Action Required';
        reminderMessage = `Dear ${member.fullName},\n\nYour gym membership payment of ₹${member.fees} was due on ${dueDate.toLocaleDateString()} and is now overdue by ${Math.abs(daysUntilDue)} days.\n\nPlease make the payment immediately to avoid membership suspension.\n\nBest regards,\nDE MUSCLE FACTORY Team`;
        break;
      case 'upcoming':
        subject = 'Payment Due Soon';
        reminderMessage = `Dear ${member.fullName},\n\nYour gym membership payment of ₹${member.fees} will be due on ${dueDate.toLocaleDateString()} (in ${daysUntilDue} days).\n\nPlease plan your payment accordingly.\n\nBest regards,\nDE MUSCLE FACTORY Team`;
        break;
      default:
        subject = 'Payment Reminder';
        reminderMessage = `Dear ${member.fullName},\n\nYour gym membership payment of ₹${member.fees} is due on ${dueDate.toLocaleDateString()}.\n\nPlease ensure timely payment.\n\nBest regards,\nDE MUSCLE FACTORY Team`;
    }
    
    // Store reminder in database
    const reminder = {
      memberId,
      memberName: member.fullName,
      memberEmail: member.email,
      memberPhone: member.phone,
      reminderType,
      subject,
      message: reminderMessage,
      dueDate: member.nextDueDate,
      fees: member.fees,
      sentDate: new Date().toISOString(),
      timestamp: Date.now(),
      status: 'sent'
    };

    const remindersRef = ref(db, tenantPath(tenantId, 'reminders'));
    const newReminderRef = push(remindersRef);
    await set(newReminderRef, reminder);

    // Send SMS reminder
    let smsResult = null;
    if (member.phone) {
      try {
        smsResult = await sendPaymentReminderSMS(member, reminderType);
        console.log('=== SMS REMINDER SENT ===');
        console.log('To:', member.phone);
        console.log('Message:', smsResult.smsMessage);
        console.log('Result:', smsResult.success ? 'Success' : 'Failed');
        console.log('========================');
      } catch (smsError) {
        console.error('SMS sending failed:', smsError);
        smsResult = { success: false, error: smsError.message };
      }
    }

    // TODO: Integrate with actual email service
    // For now, we'll simulate email sending by logging
    console.log('=== EMAIL REMINDER SENT ===');
    console.log('To:', member.email);
    console.log('Subject:', subject);
    console.log('Message:', reminderMessage);
    console.log('===========================');

    // TODO: Add actual email integration here
    // Example email integration:
    // await sendEmail(member.email, subject, reminderMessage);

    return {
      success: true,
      reminderId: newReminderRef.key,
      message: 'Payment reminder sent successfully',
      sentTo: member.email,
      subject: subject,
      smsSent: smsResult?.success || false,
      smsTo: member.phone,
      smsMessage: smsResult?.smsMessage || null
    };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

// Get payment statistics
export const getPaymentStats = async (tenantId) => {
  try {
    const paymentsRef = ref(db, tenantPath(tenantId, 'payments'));
    const membersRef = ref(db, tenantPath(tenantId, 'members'));
    
    const [paymentsSnapshot, membersSnapshot] = await Promise.all([
      get(paymentsRef),
      get(membersRef)
    ]);
    
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    
    let totalRevenue = 0;
    let monthlyRevenue = 0;
    let overdueAmount = 0;
    let upcomingAmount = 0;
    
    // Calculate revenue from payments
    if (paymentsSnapshot.exists()) {
      paymentsSnapshot.forEach((childSnapshot) => {
        const payment = childSnapshot.val();
        const paymentDate = new Date(payment.paymentDate);
        
        totalRevenue += payment.amount || 0;
        
        if (paymentDate.getMonth() === thisMonth && paymentDate.getFullYear() === thisYear) {
          monthlyRevenue += payment.amount || 0;
        }
      });
    }
    
    // Overdue and upcoming amounts, from the same accrual engine as the member profile
    let pausedMembers = 0;
    let overdueMembers = 0;

    if (membersSnapshot.exists()) {
      const sevenDaysFromNow = new Date(now.getTime() + (7 * 24 * 60 * 60 * 1000));

      membersSnapshot.forEach((childSnapshot) => {
        const member = childSnapshot.val();
        const billing = getMemberBillingSnapshot(member);

        // Paused memberships accrue nothing, so they owe nothing new
        if (billing.isPaused) {
          pausedMembers += 1;
          return;
        }

        if (billing.isOverdue) {
          overdueAmount += billing.balanceDue;
          overdueMembers += 1;
        } else if (billing.dueDate && billing.dueDate >= now && billing.dueDate <= sevenDaysFromNow) {
          upcomingAmount += billing.fees.feeAmount;
        }
      });
    }

    return {
      success: true,
      stats: {
        totalRevenue,
        monthlyRevenue,
        overdueAmount,
        upcomingAmount,
        overdueMembers,
        pausedMembers
      }
    };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

// Helper function to calculate next due date based on registration date and membership duration
const calculateNextDueDate = (registrationDate, membershipDuration) => {
  const regDate = new Date(registrationDate);
  const duration = membershipDuration.toLowerCase();
  
  switch (duration) {
    case '1 month':
    case 'monthly':
      // If registration date is 10th, due date should be 10th next month
      return new Date(regDate.getFullYear(), regDate.getMonth() + 1, regDate.getDate()).toISOString();
    case '3 months':
    case '3months':
    case 'quarterly':
      return new Date(regDate.getFullYear(), regDate.getMonth() + 3, regDate.getDate()).toISOString();
    case '6 months':
    case '6months':
    case 'semi-annually':
      return new Date(regDate.getFullYear(), regDate.getMonth() + 6, regDate.getDate()).toISOString();
    case '1 year':
    case 'annual':
    case 'annually':
    case 'yearly':
      return new Date(regDate.getFullYear() + 1, regDate.getMonth(), regDate.getDate()).toISOString();
    default:
      // Default to 1 month - same day next month
      return new Date(regDate.getFullYear(), regDate.getMonth() + 1, regDate.getDate()).toISOString();
  }
};

// Helper function to calculate next due date after payment
const calculateNextDueDateAfterPayment = (currentDueDate, registrationDate, membershipDuration) => {
  const dueDate = new Date(currentDueDate);
  const duration = membershipDuration.toLowerCase();
  
  switch (duration) {
    case '1 month':
    case 'monthly':
      // Same day next month (e.g., if due date was 10th, next due date is 10th next month)
      return new Date(dueDate.getFullYear(), dueDate.getMonth() + 1, dueDate.getDate()).toISOString();
    case '3 months':
    case '3months':
    case 'quarterly':
      return new Date(dueDate.getFullYear(), dueDate.getMonth() + 3, dueDate.getDate()).toISOString();
    case '6 months':
    case '6months':
    case 'semi-annually':
      return new Date(dueDate.getFullYear(), dueDate.getMonth() + 6, dueDate.getDate()).toISOString();
    case '1 year':
    case 'annual':
    case 'annually':
    case 'yearly':
      return new Date(dueDate.getFullYear() + 1, dueDate.getMonth(), dueDate.getDate()).toISOString();
    default:
      // Default to 1 month - same day next month
      return new Date(dueDate.getFullYear(), dueDate.getMonth() + 1, dueDate.getDate()).toISOString();
  }
};

// ==================== CONTACT FORM FUNCTIONS ====================

// Submit contact form (tenant-scoped for public site)
export const submitContactForm = async (tenantId, contactData) => {
  try {
    const contactsRef = ref(db, tenantPath(tenantId, 'contacts'));
    const newContactRef = push(contactsRef);
    await set(newContactRef, contactData);
    
    return {
      success: true,
      contactId: newContactRef.key,
      message: 'Contact form submitted successfully'
    };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

// Get all contact submissions (for admin dashboard)
export const getAllContacts = async (tenantId) => {
  try {
    const contactsRef = ref(db, tenantPath(tenantId, 'contacts'));
    const snapshot = await get(contactsRef);
    
    if (snapshot.exists()) {
      const contacts = [];
      snapshot.forEach((childSnapshot) => {
        contacts.push({
          id: childSnapshot.key,
          ...childSnapshot.val()
        });
      });
      
      // Sort by submission date (newest first)
      contacts.sort((a, b) => new Date(b.submissionDate) - new Date(a.submissionDate));
      
      return {
        success: true,
        contacts: contacts
      };
    } else {
      return {
        success: true,
        contacts: []
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

// Update contact status (mark as read, responded, etc.)
export const updateContactStatus = async (tenantId, contactId, status) => {
  try {
    const contactRef = ref(db, tenantPath(tenantId, 'contacts', contactId));
    await update(contactRef, { status, updatedAt: new Date().toISOString() });
    
    return {
      success: true,
      message: 'Contact status updated successfully'
    };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

// Delete contact submission
export const deleteContact = async (tenantId, contactId) => {
  try {
    const contactRef = ref(db, tenantPath(tenantId, 'contacts', contactId));
    await remove(contactRef);
    
    return {
      success: true,
      message: 'Contact deleted successfully'
    };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
}; 

// Get member balance information
export const getMemberBalance = async (tenantId, memberId) => {
  try {
    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const memberSnapshot = await get(memberRef);
    
    if (memberSnapshot.exists()) {
      const member = memberSnapshot.val();
      const monthlyFee = parseFloat(member.fees) || 0;
      const totalPaid = member.totalPaid || 0;
      const remainingBalance = Math.max(0, monthlyFee - totalPaid);
      const paymentStatus = remainingBalance === 0 ? 'fully_paid' : 'partial';
      
      return {
        success: true,
        balance: {
          monthlyFee,
          totalPaid,
          remainingBalance,
          paymentStatus,
          lastPaymentDate: member.lastPaymentDate,
          nextDueDate: member.nextDueDate
        }
      };
    } else {
      return {
        success: false,
        message: 'Member not found'
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

// Recalculate balances for existing members
export const recalculateMemberBalances = async (tenantId, memberId) => {
  try {
    console.log('Recalculating balances for member:', memberId);
    
    // Get all payments for this member
    const paymentsResult = await getMemberPayments(tenantId, memberId);
    if (!paymentsResult.success) {
      throw new Error('Failed to load payments for balance calculation');
    }
    
    // Get member data
    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const memberSnapshot = await get(memberRef);
    
    if (!memberSnapshot.exists()) {
      throw new Error('Member not found');
    }
    
    const member = memberSnapshot.val();
    const monthlyFee = parseFloat(member.fees) || 0;
    
    // Calculate total paid from payments
    const totalPaid = (paymentsResult.payments || []).reduce((total, payment) => {
      return total + (payment.amount || 0);
    }, 0);
    
    const remainingBalance = Math.max(0, monthlyFee - totalPaid);
    const paymentStatus = remainingBalance === 0 ? 'fully_paid' : 'partial';
    
    // Update member with calculated balances
    const balanceUpdates = {
      totalPaid,
      remainingBalance,
      paymentStatus
    };
    
    console.log('Updating member balances:', balanceUpdates);
    await update(memberRef, balanceUpdates);
    
    return {
      success: true,
      message: 'Member balances recalculated successfully',
      balance: {
        monthlyFee,
        totalPaid,
        remainingBalance,
        paymentStatus
      }
    };
  } catch (error) {
    console.error('Error recalculating member balances:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'Failed to recalculate balances'
    };
  }
};

// Recalculate due date for existing member based on registration date and membership duration
export const recalculateMemberDueDate = async (tenantId, memberId) => {
  try {
    console.log('Recalculating due date for member:', memberId);
    
    // Get member data
    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const memberSnapshot = await get(memberRef);
    
    if (!memberSnapshot.exists()) {
      throw new Error('Member not found');
    }
    
    const member = memberSnapshot.val();
    const registrationDate = member.registrationDate || new Date().toISOString();
    const membershipDuration = member.membershipDuration || 'monthly';
    
    // Calculate correct due date based on registration date and membership duration
    const correctDueDate = calculateNextDueDate(registrationDate, membershipDuration);
    
    // Update member with corrected due date
    const dueDateUpdates = {
      nextDueDate: correctDueDate
    };
    
    console.log('Updating member due date:', {
      oldDueDate: member.nextDueDate,
      newDueDate: correctDueDate,
      membershipDuration,
      registrationDate
    });
    
    await update(memberRef, dueDateUpdates);
    
    return {
      success: true,
      message: 'Member due date recalculated successfully',
      dueDate: {
        oldDueDate: member.nextDueDate,
        newDueDate: correctDueDate,
        membershipDuration,
        registrationDate
      }
    };
  } catch (error) {
    console.error('Error recalculating member due date:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'Failed to recalculate due date'
    };
  }
};

// Recalculate due dates for all existing members (useful for fixing existing data)
export const recalculateAllMemberDueDates = async (tenantId) => {
  try {
    console.log('Recalculating due dates for all members...');
    
    const membersResult = await getAllMembers(tenantId);
    if (!membersResult.success) {
      throw new Error('Failed to load members');
    }
    
    const members = membersResult.members || [];
    const results = {
      total: members.length,
      updated: 0,
      failed: 0,
      errors: []
    };
    
    for (const member of members) {
      try {
        const result = await recalculateMemberDueDate(tenantId, member.id);
        if (result.success) {
          results.updated++;
        } else {
          results.failed++;
          results.errors.push({
            memberId: member.id,
            memberName: member.fullName,
            error: result.message
          });
        }
      } catch (error) {
        results.failed++;
        results.errors.push({
          memberId: member.id,
          memberName: member.fullName,
          error: error.message
        });
      }
    }
    
    console.log('Due date recalculation completed:', results);
    
    return {
      success: true,
      message: `Recalculated due dates for ${results.updated} out of ${results.total} members`,
      results
    };
  } catch (error) {
    console.error('Error recalculating all member due dates:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'Failed to recalculate all due dates'
    };
  }
}; 

// Handle monthly billing rollover
export const handleMonthlyBillingRollover = async (tenantId, memberId) => {
  try {
    console.log('🔄 Handling monthly billing rollover for member:', memberId);
    
    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const memberSnapshot = await get(memberRef);
    
    if (!memberSnapshot.exists()) {
      throw new Error('Member not found');
    }
    
    const member = memberSnapshot.val();
    const monthlyFee = parseFloat(member.fees) || 0;
    const currentBalance = member.remainingBalance || 0;
    const lastBillingDate = member.lastBillingDate || member.registrationDate;
    
    // Check if it's time for a new monthly billing cycle
    const now = new Date();
    const lastBilling = new Date(lastBillingDate);
    const monthsSinceLastBilling = (now.getFullYear() - lastBilling.getFullYear()) * 12 + 
                                   (now.getMonth() - lastBilling.getMonth());
    
    if (monthsSinceLastBilling >= 1) {
      console.log(`Months since last billing: ${monthsSinceLastBilling}`);
      
      // Calculate new total due (previous balance + new monthly fee)
      const newTotalDue = currentBalance + monthlyFee;
      const newRemainingBalance = newTotalDue;
      
      // Update member with new billing cycle
      const billingUpdates = {
        lastBillingDate: now.toISOString(),
        totalDue: newTotalDue,
        remainingBalance: newRemainingBalance,
        paymentStatus: 'unpaid',
        billingCycle: (member.billingCycle || 0) + monthsSinceLastBilling,
        overdueAmount: currentBalance > 0 ? currentBalance : 0
      };
      
      console.log('Updating member with new billing cycle:', billingUpdates);
      await update(memberRef, billingUpdates);
      
      return {
        success: true,
        message: `Monthly billing rollover completed. New total due: ₹${newTotalDue.toLocaleString()}`,
        billing: {
          previousBalance: currentBalance,
          newMonthlyFee: monthlyFee,
          newTotalDue,
          monthsBilled: monthsSinceLastBilling
        }
      };
    } else {
      return {
        success: true,
        message: 'No billing rollover needed yet',
        billing: {
          monthsSinceLastBilling,
          nextBillingDate: new Date(lastBilling.getTime() + 30 * 24 * 60 * 60 * 1000)
        }
      };
    }
  } catch (error) {
    console.error('Error handling monthly billing rollover:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'Failed to handle billing rollover'
    };
  }
};

// Get comprehensive billing information
export const getMemberBillingInfo = async (tenantId, memberId) => {
  try {
    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const memberSnapshot = await get(memberRef);
    
    if (!memberSnapshot.exists()) {
      return {
        success: false,
        message: 'Member not found'
      };
    }
    
    const member = memberSnapshot.val();
    const monthlyFee = parseFloat(member.fees) || 0;
    const currentBalance = member.remainingBalance || 0;
    const totalPaid = member.totalPaid || 0;
    const lastBillingDate = member.lastBillingDate || member.registrationDate;
    const billingCycle = member.billingCycle || 1;
    
    // Calculate billing statistics
    const now = new Date();
    const lastBilling = new Date(lastBillingDate);
    const monthsSinceLastBilling = (now.getFullYear() - lastBilling.getFullYear()) * 12 + 
                                   (now.getMonth() - lastBilling.getMonth());
    
    const nextBillingDate = new Date(lastBilling.getTime() + 30 * 24 * 60 * 60 * 1000);
    const daysUntilNextBilling = Math.ceil((nextBillingDate - now) / (1000 * 60 * 60 * 24));
    
    return {
      success: true,
      billing: {
        monthlyFee,
        currentBalance,
        totalPaid,
        lastBillingDate,
        nextBillingDate,
        billingCycle,
        monthsSinceLastBilling,
        daysUntilNextBilling,
        isOverdue: currentBalance > 0,
        overdueAmount: currentBalance > 0 ? currentBalance : 0,
        paymentStatus: member.paymentStatus || 'unpaid'
      }
    };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
}; 

// Identify who performed a membership action (for the pause/resume audit trail)
const getActorLabel = () => auth.currentUser?.email || auth.currentUser?.uid || 'system';

// Pause a membership. Fee accrual stops on the pause date and the event is
// appended to the member's pause history.
export const pauseMembership = async (tenantId, memberId, pauseReason = 'No attendance', pauseDate = null) => {
  try {
    console.log('⏸️ Pausing membership for member:', memberId);

    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const memberSnapshot = await get(memberRef);

    if (!memberSnapshot.exists()) {
      throw new Error('Member not found');
    }

    const member = memberSnapshot.val();

    if (member.membershipStatus === 'paused' || getActivePause(member)) {
      return {
        success: false,
        error: 'already_paused',
        message: 'Membership is already paused.'
      };
    }

    const pauseStartDate = toIsoString(pauseDate) || new Date().toISOString();
    const effectiveRegistrationDate =
      toIsoString(getEffectiveRegistrationDate(member)) || pauseStartDate;

    // Pausing before the current billing start date would produce negative accrual
    if (startOfDay(pauseStartDate) < startOfDay(effectiveRegistrationDate)) {
      return {
        success: false,
        error: 'invalid_pause_date',
        message: `Pause date cannot be earlier than the current billing start date (${toDate(effectiveRegistrationDate).toLocaleDateString()}).`
      };
    }

    // Snapshot what the member owes up to the pause date - accrual stops here
    const feesAtPause = calculateMembershipFees(member, { asOfDate: pauseStartDate });

    const pauseMonthDate = toDate(pauseStartDate);
    const pauseMonthKey = `${pauseMonthDate.getFullYear()}-${String(pauseMonthDate.getMonth() + 1).padStart(2, '0')}`;

    const pauseRecord = {
      id: `pause_${Date.now()}`,
      pauseDate: pauseStartDate,
      pauseMonth: pauseMonthKey,
      reason: pauseReason || 'Not specified',
      status: PAUSE_STATUS.ACTIVE,
      pausedAt: new Date().toISOString(),
      pausedBy: getActorLabel(),
      effectiveRegistrationDateAtPause: effectiveRegistrationDate,
      chargedPeriodsAtPause: feesAtPause.periodsCharged,
      chargedAmountAtPause: feesAtPause.totalCharged,
      resumeDate: null,
      resumedAt: null,
      resumedBy: null,
      durationDays: null
    };

    const lastActiveDate =
      member.lastActiveDate ||
      member.lastPaymentDate ||
      member.joinDate ||
      member.createdAt ||
      pauseStartDate;

    const pauseUpdates = {
      membershipStatus: 'paused',
      pauseStartDate,
      pauseReason: pauseRecord.reason,
      pauseMonth: pauseMonthKey,
      lastActiveDate,
      // Preserve the original registration date for the member's history
      originalRegistrationDate: toIsoString(getOriginalRegistrationDate(member)) || effectiveRegistrationDate,
      effectiveRegistrationDate,
      pauseHistory: [...getPauseHistory(member), pauseRecord],
      // Fee calculation is stopped, so there is no upcoming due date while paused
      nextDueDate: null,
      nextBillingDate: null
    };

    console.log('Pausing membership with:', pauseUpdates);
    await update(memberRef, pauseUpdates);

    await logMemberActivity(tenantId, {
      memberId,
      memberName: member.fullName,
      type: ACTIVITY_TYPES.MEMBERSHIP_PAUSED,
      summary: `Membership paused from ${pauseMonthDate.toLocaleDateString()} — ${pauseRecord.reason}`,
      details: {
        pauseDate: pauseStartDate,
        reason: pauseRecord.reason,
        chargedUpToPause: feesAtPause.totalCharged
      }
    });

    return {
      success: true,
      message: `Membership paused on ${pauseMonthDate.toLocaleDateString()}. Fees stop accruing from this date.`,
      pause: {
        startDate: pauseStartDate,
        month: pauseMonthKey,
        reason: pauseRecord.reason,
        chargedUpToPause: feesAtPause.totalCharged,
        periodsChargedUpToPause: feesAtPause.periodsCharged
      }
    };
  } catch (error) {
    console.error('Error pausing membership:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'Failed to pause membership'
    };
  }
};

// Resume a paused membership. The resume date becomes the new effective
// registration date used for future fee calculation.
export const resumeMembership = async (tenantId, memberId, resumeDate = null) => {
  try {
    console.log('▶️ Resuming membership for member:', memberId);

    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const memberSnapshot = await get(memberRef);

    if (!memberSnapshot.exists()) {
      throw new Error('Member not found');
    }

    const member = memberSnapshot.val();
    const activePause = getActivePause(member);

    if (member.membershipStatus !== 'paused' && !activePause) {
      return {
        success: false,
        error: 'not_paused',
        message: 'Membership is not currently paused.'
      };
    }

    const resumeStartDate = toIsoString(resumeDate) || new Date().toISOString();

    if (activePause?.pauseDate && startOfDay(resumeStartDate) < startOfDay(activePause.pauseDate)) {
      return {
        success: false,
        error: 'invalid_resume_date',
        message: `Resume date cannot be earlier than the pause date (${toDate(activePause.pauseDate).toLocaleDateString()}).`
      };
    }

    // While paused, this returns everything charged up to the pause date
    const feesAtPause = calculateMembershipFees(member);
    const chargedPeriods = Number(activePause?.chargedPeriodsAtPause);
    const chargedAmount = Number(activePause?.chargedAmountAtPause);

    const completedPause = {
      ...(activePause || {}),
      id: activePause?.id || `pause_${Date.now()}`,
      pauseDate: activePause?.pauseDate || member.pauseStartDate || resumeStartDate,
      reason: activePause?.reason || member.pauseReason || 'Not specified',
      status: PAUSE_STATUS.COMPLETED,
      resumeDate: resumeStartDate,
      resumedAt: new Date().toISOString(),
      resumedBy: getActorLabel(),
      durationDays: daysBetween(activePause?.pauseDate || member.pauseStartDate, resumeStartDate),
      chargedPeriodsAtPause: Number.isFinite(chargedPeriods) ? chargedPeriods : feesAtPause.periodsCharged,
      chargedAmountAtPause: Number.isFinite(chargedAmount) ? chargedAmount : feesAtPause.totalCharged,
      newEffectiveRegistrationDate: resumeStartDate
    };
    delete completedPause.legacy;

    const history = getPauseHistory(member);
    const existingIndex = history.findIndex((entry) => entry.id === completedPause.id);
    const updatedHistory =
      existingIndex >= 0
        ? history.map((entry, index) => (index === existingIndex ? completedPause : entry))
        : [...history, completedPause];

    const durationMonths = getDurationMonths(member.membershipDuration);
    const nextDueDate = addMonths(resumeStartDate, durationMonths).toISOString();

    const resumeUpdates = {
      membershipStatus: 'active',
      pauseStartDate: null,
      pauseReason: null,
      pauseMonth: null,
      resumeDate: resumeStartDate,
      lastResumeDate: resumeStartDate,
      // Future fee calculation runs from the resume date...
      effectiveRegistrationDate: resumeStartDate,
      // ...while the original registration date is preserved
      originalRegistrationDate:
        toIsoString(getOriginalRegistrationDate(member)) || toIsoString(member.registrationDate),
      // Everything charged before the pause is carried forward
      carriedChargedPeriods: completedPause.chargedPeriodsAtPause,
      carriedChargedAmount: completedPause.chargedAmountAtPause,
      pauseHistory: updatedHistory,
      totalPausedDays: updatedHistory.reduce((total, entry) => total + (entry.durationDays || 0), 0),
      lastBillingDate: resumeStartDate,
      nextBillingDate: nextDueDate,
      nextDueDate,
      billingCycle: (member.billingCycle || 0) + 1
    };

    console.log('Resuming membership with:', resumeUpdates);
    await update(memberRef, resumeUpdates);

    await logMemberActivity(tenantId, {
      memberId,
      memberName: member.fullName,
      type: ACTIVITY_TYPES.MEMBERSHIP_RESUMED,
      summary: `Membership resumed on ${toDate(resumeStartDate).toLocaleDateString()} after ${completedPause.durationDays} day(s) paused`,
      changes: [
        {
          field: 'effectiveRegistrationDate',
          label: 'Registration Date (for fees)',
          from: toDate(resumeUpdates.originalRegistrationDate)?.toLocaleDateString() || '—',
          to: toDate(resumeStartDate).toLocaleDateString()
        }
      ],
      details: {
        resumeDate: resumeStartDate,
        pausedDays: completedPause.durationDays,
        nextDueDate
      }
    });

    return {
      success: true,
      message: 'Membership resumed successfully. Fees now accrue from the resume date.',
      resume: {
        resumeDate: resumeStartDate,
        nextBillingDate: nextDueDate,
        newBillingCycle: resumeUpdates.billingCycle,
        pausedDays: completedPause.durationDays,
        effectiveRegistrationDate: resumeStartDate,
        originalRegistrationDate: resumeUpdates.originalRegistrationDate
      }
    };
  } catch (error) {
    console.error('Error resuming membership:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'Failed to resume membership'
    };
  }
};

// Enhanced monthly billing rollover with pause handling
export const handleMonthlyBillingRolloverWithPause = async (tenantId, memberId) => {
  try {
    console.log('🔄 Handling monthly billing rollover with pause check for member:', memberId);
    
    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const memberSnapshot = await get(memberRef);
    
    if (!memberSnapshot.exists()) {
      throw new Error('Member not found');
    }
    
    const member = memberSnapshot.val();
    
    // Check if membership is paused
    if (member.membershipStatus === 'paused') {
      console.log('Membership is paused, skipping billing for this month');
      return {
        success: true,
        message: 'Membership is paused. No fees charged this month.',
        billing: {
          status: 'paused',
          reason: member.pauseReason,
          pauseMonth: member.pauseMonth
        }
      };
    }
    
    const monthlyFee = parseFloat(member.fees) || 0;
    const currentBalance = member.remainingBalance || 0;
    const lastBillingDate = member.lastBillingDate || member.registrationDate;
    
    // Check if it's time for a new monthly billing cycle
    const now = new Date();
    const lastBilling = new Date(lastBillingDate);
    const monthsSinceLastBilling = (now.getFullYear() - lastBilling.getFullYear()) * 12 + 
                                   (now.getMonth() - lastBilling.getMonth());
    
    if (monthsSinceLastBilling >= 1) {
      console.log(`Months since last billing: ${monthsSinceLastBilling}`);
      
      // Calculate new total due (previous balance + new monthly fee)
      const newTotalDue = currentBalance + monthlyFee;
      const newRemainingBalance = newTotalDue;
      
      // Update member with new billing cycle
      const billingUpdates = {
        lastBillingDate: now.toISOString(),
        totalDue: newTotalDue,
        remainingBalance: newRemainingBalance,
        paymentStatus: 'unpaid',
        billingCycle: (member.billingCycle || 0) + monthsSinceLastBilling,
        overdueAmount: currentBalance > 0 ? currentBalance : 0
      };
      
      console.log('Updating member with new billing cycle:', billingUpdates);
      await update(memberRef, billingUpdates);
      
      return {
        success: true,
        message: `Monthly billing rollover completed. New total due: ₹${newTotalDue.toLocaleString()}`,
        billing: {
          previousBalance: currentBalance,
          newMonthlyFee: monthlyFee,
          newTotalDue,
          monthsBilled: monthsSinceLastBilling,
          status: 'active'
        }
      };
    } else {
      return {
        success: true,
        message: 'No billing rollover needed yet',
        billing: {
          monthsSinceLastBilling,
          nextBillingDate: new Date(lastBilling.getTime() + 30 * 24 * 60 * 60 * 1000),
          status: 'active'
        }
      };
    }
  } catch (error) {
    console.error('Error handling monthly billing rollover with pause:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'Failed to handle billing rollover'
    };
  }
};

// Get membership pause information
export const getMembershipPauseInfo = async (tenantId, memberId) => {
  try {
    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const memberSnapshot = await get(memberRef);
    
    if (!memberSnapshot.exists()) {
      return {
        success: false,
        message: 'Member not found'
      };
    }
    
    const member = memberSnapshot.val();
    const history = getPauseHistory(member);
    const activePause = getActivePause(member);
    const isPaused = member.membershipStatus === 'paused' || Boolean(activePause);
    const fees = calculateMembershipFees(member);

    return {
      success: true,
      pauseInfo: {
        isPaused,
        status: member.membershipStatus || 'active',
        pauseStartDate: activePause?.pauseDate || member.pauseStartDate || null,
        pauseReason: activePause?.reason || member.pauseReason || null,
        pauseMonth: member.pauseMonth || null,
        resumeDate: member.resumeDate || null,
        lastActiveDate: member.lastActiveDate || null,
        nextBillingDate: member.nextBillingDate || null,
        history,
        activePause,
        totalPauses: history.length,
        totalPausedDays: history.reduce((total, entry) => total + (entry.durationDays || 0), 0),
        originalRegistrationDate: toIsoString(getOriginalRegistrationDate(member)),
        effectiveRegistrationDate: toIsoString(getEffectiveRegistrationDate(member)),
        fees
      }
    };
  } catch (error) {
    return {
      success: false,
      error: error.code,
      message: getErrorMessage(error.code)
    };
  }
};

// Fields owned by the pause/resume + billing engine. Staff edits must never
// overwrite them, so a member can be edited while active, paused or resumed.
const MEMBERSHIP_STATE_FIELDS = [
  'id',
  'membershipStatus',
  'pauseHistory',
  'pauseStartDate',
  'pauseReason',
  'pauseMonth',
  'resumeDate',
  'lastResumeDate',
  'totalPausedDays',
  'carriedChargedPeriods',
  'carriedChargedAmount',
  'effectiveRegistrationDate',
  'originalRegistrationDate',
  'totalPaid',
  'totalDue',
  'remainingBalance',
  'overdueAmount',
  'paymentStatus',
  'lastPaymentDate',
  'lastPaymentAmount',
  'billingCycle',
  'lastBillingDate',
  'nextBillingDate',
  'nextDueDate',
  'lastActiveDate',
  'updatedAt',
  'updatedBy'
];

// Update member details regardless of membership status (active/paused/resumed)
export const updateMemberDetails = async (tenantId, memberId, updates) => {
  try {
    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const memberSnapshot = await get(memberRef);

    if (!memberSnapshot.exists()) {
      return {
        success: false,
        error: 'not_found',
        message: 'Member not found'
      };
    }

    const member = memberSnapshot.val();

    const safeUpdates = Object.fromEntries(
      Object.entries(updates || {}).filter(
        ([key, value]) => !MEMBERSHIP_STATE_FIELDS.includes(key) && value !== undefined
      )
    );

    const isPaused = member.membershipStatus === 'paused' || Boolean(getActivePause(member));
    const hasPauseHistory = getPauseHistory(member).length > 0;

    // Changing the registration date only re-anchors billing when the member has
    // never been paused - otherwise the resume date remains the billing anchor.
    if (safeUpdates.registrationDate) {
      safeUpdates.originalRegistrationDate = toIsoString(safeUpdates.registrationDate);

      if (!hasPauseHistory && !isPaused) {
        safeUpdates.effectiveRegistrationDate = toIsoString(safeUpdates.registrationDate);
      }
    }

    // Keep the due date in step with an edited registration date or duration
    if (!isPaused && (safeUpdates.registrationDate || safeUpdates.membershipDuration)) {
      const anchor =
        safeUpdates.effectiveRegistrationDate ||
        toIsoString(getEffectiveRegistrationDate(member)) ||
        toIsoString(safeUpdates.registrationDate);
      const duration = safeUpdates.membershipDuration || member.membershipDuration || 'monthly';

      if (anchor) {
        safeUpdates.nextDueDate = calculateNextDueDate(anchor, duration);
      }
    }

    // Capture what actually changed before writing
    const changes = diffMemberDetails(member, safeUpdates);

    safeUpdates.updatedAt = new Date().toISOString();
    safeUpdates.updatedBy = getActorLabel();

    await update(memberRef, safeUpdates);

    if (changes.length > 0) {
      await logMemberActivity(tenantId, {
        memberId,
        memberName: safeUpdates.fullName || member.fullName,
        type: ACTIVITY_TYPES.DETAILS_UPDATED,
        summary: `Updated ${changes.map((change) => change.label).join(', ')}`,
        changes
      });
    }

    return {
      success: true,
      message: 'Member details updated successfully',
      membershipStatus: member.membershipStatus || 'active',
      changes
    };
  } catch (error) {
    console.error('Error updating member details:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || getErrorMessage(error.code) || 'Failed to update member details'
    };
  }
};